import { useState } from 'react';
import { DEFAULT_SLEEP_METHODS, activeMinutes, can, describeEvent, formatClock, formatTimer, type BabyEvent } from '@bb/shared/core';
import { Icon, TYPE_ICON, useNow } from '../components';
import { DurationWheel, formatIn } from '../Wheel';
import { cancelSettling, currentRunSec, currentSide, fellAsleep, finishBottle, isPaused, pauseBreast, resumeBreast, runsOf, setAlarm, setSideTarget, stopTimer, switchBreast, toggleMethod } from '../actions';
import { categoryOf } from '../lib';
import { useApp } from '../state';

/** Панели идущих таймеров — на «Сегодня», в шторке действия и (кратко) в плашке сверху. */

export const RUNNING_TITLE: Record<string, string> = { breast: 'Кормление грудью', bottle: 'Бутылочка', sleep: 'Сон', walk: 'Прогулка' };
const SIDE: Record<string, string> = { left: 'Левая', right: 'Правая' };

/** Напоминание по умолчанию (секунды) и вид барабана: минуты/секунды для кормлений, часы/минуты для сна и прогулки. */
export const ALARM_DEFAULT: Record<string, { sec: number; mode: 'ms' | 'hm' }> = {
  breast: { sec: 15 * 60, mode: 'ms' },
  bottle: { sec: 10 * 60, mode: 'ms' },
  sleep: { sec: 90 * 60, mode: 'hm' },
  walk: { sec: 60 * 60, mode: 'hm' },
};

/** Секунды «чистого» времени таймера (без пауз). */
export function activeSeconds(e: BabyEvent, now: number) {
  return Math.floor(activeMinutes(e, now) * 60);
}

/** Остаток обратного отсчёта в секундах (на паузе — замирает). null — таймер не поставлен. */
export function countdownLeft(e: BabyEvent, now: number): number | null {
  // Грудь с таймером «на сторону»: осталось = цель − время текущего подхода (на паузе замирает само).
  if (e.type === 'breast' && typeof e.payload.sideTargetSec === 'number') return Math.round(e.payload.sideTargetSec - currentRunSec(e, now));
  if (!e.payload.alarmAt) return null;
  const at = Date.parse(String(e.payload.alarmAt));
  const ref = e.payload.pausedAt ? Date.parse(String(e.payload.pausedAt)) : now;
  return Math.round((at - ref) / 1000);
}

/**
 * Таймер-напоминание как кухонный: выставили 15:00 — идёт обратный отсчёт; в ноль — сигнал на экране и сообщение от бота.
 */
function AlarmLine({ e }: { e: BabyEvent }) {
  const ctx = useApp();
  const [open, setOpen] = useState(false);
  const def = ALARM_DEFAULT[e.type] ?? ALARM_DEFAULT.walk!;
  const [sec, setSec] = useState(def.sec);
  const now = useNow(1000);
  const isBreast = e.type === 'breast';
  const left = countdownLeft(e, now);
  const total = isBreast && typeof e.payload.sideTargetSec === 'number'
    ? e.payload.sideTargetSec
    : e.payload.alarmAt && e.payload.alarmFrom ? (Date.parse(String(e.payload.alarmAt)) - Date.parse(String(e.payload.alarmFrom))) / 1000 : null;
  const share = left != null && total ? Math.max(0, Math.min(1, left / total)) : null;
  return (
    <div className="stack" style={{ gap: 8 }}>
      {left != null ? (
        <button type="button" className={`countdown${left <= 0 ? ' done' : ''}`} onClick={() => setOpen(!open)} aria-expanded={open} data-testid="countdown">
          <Icon name="bell" size={18} />
          <span className="grow" style={{ textAlign: 'left' }}>
            <span className="tiny" style={{ display: 'block' }}>
              {left <= 0 ? 'Время вышло' : e.payload.pausedAt ? 'Таймер на паузе' : isBreast && total ? `Таймер на грудь ${formatTimer(total)} · осталось` : 'Таймер · осталось'}
            </span>
            <b className="countdown-num">{left <= 0 ? '00:00' : formatTimer(left)}</b>
          </span>
          <span className="tiny">изменить</span>
          {share != null && <i className="countdown-bar" style={{ width: `${share * 100}%` }} />}
        </button>
      ) : (
        <button type="button" className="alarm-line" onClick={() => setOpen(!open)} aria-expanded={open}>
          <Icon name="bell" size={16} />
          <span className="grow">{isBreast ? 'Таймер на грудь' : 'Поставить таймер'}</span>
          <Icon name={open ? 'up' : 'down'} size={14} />
        </button>
      )}
      {open && (
        <div className="stack alarm-box" style={{ gap: 8 }}>
          {isBreast && <span className="tiny">Сколько всего на эту грудь — уже прошедшее время засчитывается. После смены груди отсчёт начнётся заново.</span>}
          <DurationWheel seconds={sec} onChange={setSec} mode={def.mode} />
          <div className="tiles2">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setOpen(false);
                void (isBreast ? setSideTarget(ctx, e, null) : setAlarm(ctx, e, null));
              }}
            >
              {left != null ? 'Убрать таймер' : 'Отмена'}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={sec <= 0}
              onClick={() => {
                setOpen(false);
                void (isBreast ? setSideTarget(ctx, e, sec) : setAlarm(ctx, e, sec, formatIn(sec)));
              }}
            >
              {isBreast ? `На грудь ${formatTimer(sec)}` : `Запустить ${formatTimer(sec)}`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function BreastPanel({ e, canWrite }: { e: BabyEvent; canWrite: boolean }) {
  const ctx = useApp();
  const now = useNow();
  const paused = isPaused(e);
  const side = currentSide(e);
  const runs = runsOf(e, now);
  return (
    <section className="hero cat-feed" aria-label="Идёт кормление грудью" data-testid="running-breast">
      <div className="row">
        <Icon name="breast" />
        <span className="label grow">{paused ? `Пауза · ${SIDE[side]!.toLowerCase()} грудь` : `${SIDE[side]} грудь`}</span>
        <span className="tiny">с {formatClock(Date.parse(e.startedAt), ctx.family.tz)}</span>
      </div>
      {/* Крупно — время этой груди (после смены — с нуля), мелко — всего */}
      <div className={`timer${paused ? ' paused' : ''}`} data-testid="timer-breast">
        {formatTimer(currentRunSec(e, now))}
      </div>
      <div className="runs" data-testid="breast-runs">
        <span className="runs-total">
          Всего <b>{formatTimer(activeSeconds(e, now))}</b>
        </span>
        {runs.map((r, i) => (
          <span key={i} className={i === runs.length - 1 && !paused ? 'on' : ''}>
            {SIDE[r.side]} {formatTimer(r.sec)}
          </span>
        ))}
      </div>
      {canWrite && (
        <>
          <div className="tiles3">
            {paused ? (
              <button type="button" className="btn btn-secondary" onClick={() => void resumeBreast(ctx, e)}>
                <Icon name="play" size={18} /> Дальше
              </button>
            ) : (
              <button type="button" className="btn btn-secondary" onClick={() => void pauseBreast(ctx, e)}>
                <Icon name="pause" size={18} /> Пауза
              </button>
            )}
            <button type="button" className="btn btn-secondary" onClick={() => void switchBreast(ctx, e)}>
              <Icon name="swap" size={18} /> {side === 'left' ? 'Правая' : 'Левая'}
            </button>
            <button type="button" className="btn btn-primary" onClick={() => void stopTimer(ctx, e)}>
              <Icon name="stop" size={18} /> Стоп
            </button>
          </div>
          <AlarmLine e={e} />
        </>
      )}
    </section>
  );
}

/**
 * Компактная карточка-стикер идущего таймера: тот же цветной фон, что и у большой, — для шапки при прокрутке
 * и для других вкладок. Нажатие — полное управление.
 */
export function TimerSticker({ e }: { e: BabyEvent }) {
  const ctx = useApp();
  const now = useNow();
  const settling = e.type === 'sleep' && Boolean(e.payload.settling);
  const paused = e.type === 'breast' && isPaused(e);
  const big = e.type === 'breast' ? currentRunSec(e, now) : activeSeconds(e, now);
  const title =
    e.type === 'breast'
      ? `${paused ? 'Пауза · ' : ''}${SIDE[currentSide(e)]} грудь`
      : e.type === 'bottle'
        ? `Бутылочка ${String(e.payload.preparedMl ?? e.payload.ml)} мл`
        : settling
          ? 'Укладываем'
          : e.type === 'sleep'
            ? 'Спит'
            : 'Прогулка';
  const left = countdownLeft(e, now);
  const canWrite = can(ctx.family.role, 'events.write');
  return (
    <div className={`sticker cat-${categoryOf(e.type)}`} data-testid={`bar-${e.type}`}>
      <button type="button" className="sticker-main" onClick={() => ctx.openAction(e.type, e)} aria-label={`${title}: управление`}>
        <Icon name={TYPE_ICON[e.type]} size={22} />
        <span className="grow" style={{ textAlign: 'left', minWidth: 0 }}>
          <span className="sticker-title">{title}</span>
          <span className="tiny" style={{ display: 'block' }}>
            {e.type === 'breast' ? `всего ${formatTimer(activeSeconds(e, now))}` : `с ${formatClock(Date.parse(e.startedAt), ctx.family.tz)}`}
          </span>
          {left != null && (
            <span className={`bar-cd${left <= 0 ? ' done' : ''}`}>
              <Icon name="bell" size={11} /> {left <= 0 ? 'время вышло' : formatTimer(left)}
            </span>
          )}
        </span>
        <b className={`sticker-time${paused ? ' paused' : ''}`}>{formatTimer(big)}</b>
      </button>
      {canWrite && e.type === 'breast' && (
        <button type="button" className="sticker-btn" aria-label="Сменить грудь" onClick={() => void switchBreast(ctx, e)}>
          <Icon name="swap" size={18} />
        </button>
      )}
      {canWrite && e.type !== 'bottle' && !settling && (
        <button type="button" className="sticker-btn" aria-label="Стоп" onClick={() => void stopTimer(ctx, e)}>
          <Icon name="stop" size={18} />
        </button>
      )}
    </div>
  );
}

export function BottlePanel({ e, canWrite }: { e: BabyEvent; canWrite: boolean }) {
  const ctx = useApp();
  const now = useNow();
  const prepared = Number(e.payload.preparedMl ?? e.payload.ml) || 0;
  const [finishing, setFinishing] = useState(false);
  const [left, setLeft] = useState(0);
  return (
    <section className="hero cat-feed" aria-label="Идёт кормление из бутылочки" data-testid="running-bottle">
      <div className="row">
        <Icon name="bottle" />
        <span className="label grow">
          Бутылочка · {prepared} мл{e.payload.kind === 'breast_milk' ? ' · сцеженное' : ''}
        </span>
        <span className="tiny">с {formatClock(Date.parse(e.startedAt), ctx.family.tz)}</span>
      </div>
      <div className="timer" data-testid="timer-bottle">
        {formatTimer(activeSeconds(e, now))}
      </div>
      {canWrite && !finishing && (
        <>
          <button type="button" className="btn btn-primary" onClick={() => setFinishing(true)}>
            <Icon name="stop" size={18} /> Поел — стоп
          </button>
          <AlarmLine e={e} />
        </>
      )}
      {canWrite && finishing && (
        <div className="stack" style={{ gap: 10 }}>
          <span className="label">Сколько осталось в бутылочке?</span>
          <div className="stepper">
            <button type="button" aria-label="Меньше на 10 мл" onClick={() => setLeft(Math.max(0, left - 10))}>
              −
            </button>
            <span className="num" data-testid="left-ml">
              {left}
            </span>
            <button type="button" className="plus" aria-label="Больше на 10 мл" onClick={() => setLeft(Math.min(prepared, left + 10))}>
              +
            </button>
          </div>
          <div className="small" style={{ textAlign: 'center' }}>
            Выпил <b>{prepared - left} мл</b> из {prepared}
          </div>
          <div className="tiles2">
            <button type="button" className="btn btn-secondary" onClick={() => setFinishing(false)}>
              Ещё ест
            </button>
            <button type="button" className="btn btn-primary" onClick={() => void finishBottle(ctx, e, left)}>
              Записать
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

export function SimplePanel({ e, canWrite }: { e: BabyEvent; canWrite: boolean }) {
  const ctx = useApp();
  const now = useNow();
  const cat = categoryOf(e.type);
  const details = e.type === 'sleep' ? describeEvent(e).replace(/^Сон( · )?/, '') : '';
  return (
    <section className={`hero cat-${cat}`} aria-label={`Идёт: ${RUNNING_TITLE[e.type]}`}>
      <div className="row">
        <Icon name={TYPE_ICON[e.type]} />
        <span className="label grow">{e.type === 'sleep' ? 'Спит' : RUNNING_TITLE[e.type]}</span>
        <span className="tiny">с {formatClock(Date.parse(e.startedAt), ctx.family.tz)}</span>
      </div>
      <div className="timer" data-testid={`timer-${e.type}`}>
        {formatTimer(activeSeconds(e, now))}
      </div>
      {details && <span className="small">{details}</span>}
      {canWrite && (
        <>
          <button type="button" className="btn btn-primary" onClick={() => void stopTimer(ctx, e)}>
            <Icon name="stop" size={18} /> {e.type === 'sleep' ? 'Проснулся' : e.type === 'walk' ? 'Вернулись домой' : 'Стоп'}
          </button>
          <AlarmLine e={e} />
        </>
      )}
    </section>
  );
}

/** Укладывание: сколько уже укладываем, что делаем, «Уснул!». */
export function SettlePanel({ e, canWrite }: { e: BabyEvent; canWrite: boolean }) {
  const ctx = useApp();
  const now = useNow();
  const fs = ctx.family.settings ?? {};
  const methods = fs.sleepMethods?.length ? fs.sleepMethods : DEFAULT_SLEEP_METHODS;
  const chosen = Array.isArray(e.payload.methods) ? (e.payload.methods as string[]) : [];
  return (
    <section className="hero cat-sleep" aria-label="Идёт укладывание" data-testid="running-settle">
      <div className="row">
        <Icon name="sleep" />
        <span className="label grow">Укладываем</span>
        <span className="tiny">с {formatClock(Date.parse(e.startedAt), ctx.family.tz)}</span>
      </div>
      <div className="timer" data-testid="timer-settle">
        {formatTimer(activeSeconds(e, now))}
      </div>
      {canWrite && (
        <>
          <div className="field">
            <span className="label">Что делаем (можно несколько)</span>
            <div className="chips" role="group" aria-label="Что делаем">
              {methods.map((m) => (
                <button key={m} type="button" className="chip" aria-pressed={chosen.includes(m)} onClick={() => void toggleMethod(ctx, e, m)}>
                  {m}
                </button>
              ))}
            </div>
          </div>
          <button type="button" className="btn btn-primary btn-block" onClick={() => void fellAsleep(ctx, e)} data-testid="fell-asleep">
            <Icon name="sleep" size={18} /> Уснул!
          </button>
          <button type="button" className="link-btn" onClick={() => void cancelSettling(ctx, e)}>
            Не уснул — отменить
          </button>
        </>
      )}
    </section>
  );
}

export function RunningPanel({ e, canWrite }: { e: BabyEvent; canWrite: boolean }) {
  if (e.type === 'sleep' && e.payload.settling) return <SettlePanel e={e} canWrite={canWrite} />;
  if (e.type === 'breast') return <BreastPanel e={e} canWrite={canWrite} />;
  if (e.type === 'bottle') return <BottlePanel e={e} canWrite={canWrite} />;
  return <SimplePanel e={e} canWrite={canWrite} />;
}
